import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Home as HomeIcon,
  Users,
  HeartHandshake,
  Building2,
  Loader2,
  CheckCircle2,
  Shield,
  Handshake,
  Sparkles,
  MapPin,
} from "lucide-react";
import { api } from "@/lib/api";

// --------------------- Veteran Interest Form ---------------------
const HOUSING_NEEDS = [
  "Apartment",
  "Single Family Home",
  "Temporary Housing Resources",
  "Affordable Housing Options",
];

function VeteranForm() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    city_state: "",
    bedrooms: "",
    message: "",
  });
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNeed = (n) =>
    setNeeds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Please add your name and phone.");
      return;
    }
    setLoading(true);
    try {
      const detailsMsg =
        `Housing needs: ${needs.join(", ") || "—"}\n` +
        `Bedrooms needed: ${form.bedrooms || "—"}\n` +
        `City/State needed: ${form.city_state || "—"}\n\n` +
        `Additional info:\n${form.message || "—"}`;
      const payload = {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email || null,
        situation: "veteran_housing_seeker",
        property_address: form.city_state || null,
        message: detailsMsg,
        source: "veteran_housing",
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thank you for your service. We'll reach out with resources shortly.");
    } catch (err) {
      const d = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof d === "string" ? d : "Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="vet-seeker-success"
        className="rounded-lg bg-white border border-[#E5E3DB] p-8 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-[#2C5545] mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#0B1B3D] mb-2">
          Thank you for your service.
        </h3>
        <p className="text-[#64748B] text-sm leading-relaxed">
          Our housing navigator will contact you with available resources and
          referrals in your area. For immediate help, call{" "}
          <span className="font-medium text-[#0B1B3D]">1-659-256-3484</span>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="vet-seeker-form"
      className="rounded-lg bg-white border border-[#E5E3DB] p-7 lg:p-8 shadow-[0_20px_60px_rgba(11,27,61,0.10)]"
    >
      <div className="flex items-center gap-2 mb-1">
        <Shield className="h-4 w-4 text-[#2C5545]" />
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-medium">
          Veteran Housing Interest
        </span>
      </div>
      <h3 className="font-display text-2xl lg:text-3xl text-[#0B1B3D] mb-1">
        Looking for housing resources?
      </h3>
      <p className="text-sm text-[#64748B] mb-6">
        Complete the form below. A housing navigator will reach out with connections
        and resources.
      </p>

      <div className="grid grid-cols-1 gap-4">
        <Field label="Full Name *">
          <Input
            data-testid="vet-name"
            value={form.full_name}
            onChange={(e) => upd("full_name", e.target.value)}
            placeholder="Jane Doe"
            required
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone *">
            <Input
              data-testid="vet-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value)}
              placeholder="(555) 555-0110"
              required
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="vet-email"
              type="email"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
              placeholder="you@email.com"
            />
          </Field>
        </div>
        <Field label="City and State Needed">
          <Input
            data-testid="vet-city"
            value={form.city_state}
            onChange={(e) => upd("city_state", e.target.value)}
            placeholder="Athens, GA"
          />
        </Field>
        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">Housing Need</Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {HOUSING_NEEDS.map((n) => {
              const checked = needs.includes(n);
              return (
                <label
                  key={n}
                  data-testid={`vet-need-${n.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors ${
                    checked ? "border-[#2C5545] bg-[#2C5545]/5 text-[#0B1B3D]" : "border-[#E5E3DB] hover:border-[#2C5545]/40 text-[#64748B]"
                  }`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleNeed(n)} />
                  <span>{n}</span>
                </label>
              );
            })}
          </div>
        </div>
        <Field label="Number of Bedrooms Needed">
          <Input
            data-testid="vet-beds"
            value={form.bedrooms}
            onChange={(e) => upd("bedrooms", e.target.value)}
            placeholder="2"
          />
        </Field>
        <Field label="Additional Information">
          <Textarea
            data-testid="vet-message"
            value={form.message}
            onChange={(e) => upd("message", e.target.value)}
            placeholder="Household size, timing, specific needs, dependents..."
            className="min-h-[90px]"
          />
        </Field>

        <button
          type="submit"
          disabled={loading}
          data-testid="vet-submit"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-lg bg-[#2C5545] hover:bg-[#3A6E5A] disabled:opacity-60 text-white font-semibold transition-all shadow-md hover:shadow-lg"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Request Housing Resources"}
        </button>
        <p className="text-[11px] text-[#64748B] text-center leading-relaxed">
          Free. Confidential. We do not guarantee placement — we help connect you with resources.
        </p>
      </div>
    </form>
  );
}

// --------------------- Housing Provider (Landlord/Investor) Form ---------------------
const PROPERTY_TYPES = ["Single Family Home", "Apartment", "Multifamily Property", "Other"];
const AVAILABILITY = [
  { v: "Yes", label: "Yes, currently available" },
  { v: "No", label: "Not right now" },
  { v: "Future Availability", label: "Future availability" },
];

function ProviderForm() {
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    phone: "",
    email: "",
    property_location: "",
    property_type: "",
    availability: "",
    unit_count: "",
    veteran_interest: "Yes",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Please add your name and phone.");
      return;
    }
    setLoading(true);
    try {
      const detailsMsg =
        `Property type: ${form.property_type || "—"}\n` +
        `Location: ${form.property_location || "—"}\n` +
        `Availability: ${form.availability || "—"}\n` +
        `Units available: ${form.unit_count || "—"}\n` +
        `Interested in veteran housing: ${form.veteran_interest}\n\n` +
        `Additional info:\n${form.message || "—"}`;
      const payload = {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email || null,
        situation: "veteran_housing_partner",
        brokerage: form.company || null,
        property_address: form.property_location || null,
        message: detailsMsg,
        source: "veteran_housing_provider",
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thank you! Our partnerships team will reach out.");
    } catch (err) {
      const d = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof d === "string" ? d : "Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="vet-provider-success"
        className="rounded-lg bg-white border border-[#E5E3DB] p-8 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-[#2C5545] mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#0B1B3D] mb-2">Welcome, partner.</h3>
        <p className="text-[#64748B] text-sm leading-relaxed">
          Our partnerships team will reach out within one business day to walk
          through how we work together.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="vet-provider-form"
      className="rounded-lg bg-white border border-[#E5E3DB] p-7 lg:p-8 shadow-[0_20px_60px_rgba(11,27,61,0.10)]"
    >
      <div className="flex items-center gap-2 mb-1">
        <Handshake className="h-4 w-4 text-[#2C5545]" />
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-medium">
          Housing Provider Interest
        </span>
      </div>
      <h3 className="font-display text-2xl lg:text-3xl text-[#0B1B3D] mb-1">
        Become a Housing Resource Partner
      </h3>
      <p className="text-sm text-[#64748B] mb-6">
        Landlords, investors, apartment communities, developers, and property
        managers — help us house those who served.
      </p>

      <div className="grid grid-cols-1 gap-4">
        <Field label="Name *">
          <Input
            data-testid="prov-name"
            value={form.full_name}
            onChange={(e) => upd("full_name", e.target.value)}
            placeholder="Alex Carter"
            required
          />
        </Field>
        <Field label="Company / Property Name">
          <Input
            data-testid="prov-company"
            value={form.company}
            onChange={(e) => upd("company", e.target.value)}
            placeholder="Carter Property Group"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone *">
            <Input
              data-testid="prov-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value)}
              required
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="prov-email"
              type="email"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Property Location (City, State)">
          <Input
            data-testid="prov-location"
            value={form.property_location}
            onChange={(e) => upd("property_location", e.target.value)}
            placeholder="Athens, GA"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">Property Type</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {PROPERTY_TYPES.map((t) => (
                <label
                  key={t}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors ${
                    form.property_type === t ? "border-[#2C5545] bg-[#2C5545]/5 text-[#0B1B3D]" : "border-[#E5E3DB] hover:border-[#2C5545]/40 text-[#64748B]"
                  }`}
                >
                  <input
                    type="radio"
                    name="prov-ptype"
                    checked={form.property_type === t}
                    onChange={() => upd("property_type", t)}
                    className="accent-[#2C5545]"
                  />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">Availability</Label>
            <div className="mt-2 grid grid-cols-1 gap-2">
              {AVAILABILITY.map((a) => (
                <label
                  key={a.v}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors ${
                    form.availability === a.v ? "border-[#2C5545] bg-[#2C5545]/5 text-[#0B1B3D]" : "border-[#E5E3DB] hover:border-[#2C5545]/40 text-[#64748B]"
                  }`}
                >
                  <input
                    type="radio"
                    name="prov-avail"
                    checked={form.availability === a.v}
                    onChange={() => upd("availability", a.v)}
                    className="accent-[#2C5545]"
                  />
                  <span>{a.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <Field label="Number of Units Available">
          <Input
            data-testid="prov-units"
            value={form.unit_count}
            onChange={(e) => upd("unit_count", e.target.value)}
            placeholder="1"
          />
        </Field>
        <Field label="Additional Information">
          <Textarea
            data-testid="prov-message"
            value={form.message}
            onChange={(e) => upd("message", e.target.value)}
            placeholder="Rent range, special programs accepted, community info..."
            className="min-h-[90px]"
          />
        </Field>

        <button
          type="submit"
          disabled={loading}
          data-testid="prov-submit"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-lg bg-[#C5A059] hover:bg-[#B38F4E] disabled:opacity-60 text-[#0B1B3D] font-semibold transition-all shadow-md hover:shadow-lg"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Become a Housing Partner"}
        </button>
      </div>
    </form>
  );
}

// --------------------- Main Section ---------------------
const PROCESS_STEPS = [
  { icon: Handshake, t: "Build housing partnerships", d: "We connect with property owners, landlords, investors, and housing providers with available rental opportunities." },
  { icon: HomeIcon, t: "Identify available housing", d: "Housing partners share info on rental homes, apartments, multifamily properties, and future developments." },
  { icon: Users, t: "Connect resources", d: "When opportunities become available, we connect them with veterans, housing programs, organizations, and referrals." },
  { icon: HeartHandshake, t: "Support the housing process", d: "Our navigators communicate with providers, coordinate info, support placement, and organize next steps." },
];

const PARTNER_BENEFITS = [
  "Support veteran housing stability",
  "Increase awareness of available rental opportunities",
  "Build community relationships",
  "Connect with veteran-serving organizations",
  "Contribute to stronger housing solutions",
];

export default function VeteranHousing() {
  const [tab, setTab] = useState("veteran");

  return (
    <section
      id="veterans"
      data-testid="veteran-housing-section"
      className="py-20 md:py-32 bg-[#FAF9F6] relative overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#2C5545]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#C5A059]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="mb-4 flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#2C5545]" />
            <span className="text-xs uppercase tracking-[0.24em] text-[#2C5545] font-medium">
              Veteran Housing Provider Program
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B1B3D] leading-tight mb-5">
            Expanding housing opportunities <em className="text-[#C5A059] not-italic font-medium">for those who served.</em>
          </h2>
          <p className="text-[#64748B] leading-relaxed text-base lg:text-lg">
            We're developing a nationwide network of housing providers, landlords,
            property owners, and investors dedicated to increasing access to safe
            and stable housing for veterans. Through community partnerships and
            housing navigation, we connect veterans with quality housing options.
          </p>
        </div>

        {/* Process strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {PROCESS_STEPS.map((s, i) => (
            <div
              key={i}
              data-testid={`vet-step-${i}`}
              className="rounded-lg border border-[#E5E3DB] bg-white p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-md bg-[#2C5545]/10 flex items-center justify-center">
                  <s.icon className="h-4 w-4 text-[#2C5545]" />
                </div>
                <span className="font-display text-xl text-[#C5A059]">0{i + 1}</span>
              </div>
              <h3 className="font-display text-lg text-[#0B1B3D] mb-1 leading-snug">{s.t}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>

        {/* Dual-audience tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            data-testid="vet-tabs"
            className="bg-white border border-[#E5E3DB] p-1.5 h-auto flex flex-wrap gap-1 mb-8 rounded-lg"
          >
            <TabsTrigger
              value="veteran"
              data-testid="vet-tab-veteran"
              className="data-[state=active]:bg-[#2C5545] data-[state=active]:text-white data-[state=inactive]:text-[#0B1B3D] px-5 py-3 rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Shield className="h-4 w-4" />
              I'm a Veteran seeking housing
            </TabsTrigger>
            <TabsTrigger
              value="provider"
              data-testid="vet-tab-provider"
              className="data-[state=active]:bg-[#2C5545] data-[state=active]:text-white data-[state=inactive]:text-[#0B1B3D] px-5 py-3 rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Building2 className="h-4 w-4" />
              I have housing to offer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="veteran" className="mt-0 focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-lg border border-[#2C5545]/30 bg-[#2C5545]/5 p-6">
                  <h3 className="font-display text-2xl text-[#0B1B3D] mb-3">
                    We navigate. You get options.
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed mb-4">
                    Our housing navigators help by communicating with providers,
                    coordinating information, supporting placement, and helping
                    organize each step in the housing process.
                  </p>
                  <ul className="space-y-2 text-sm text-[#0B1B3D]">
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#C5A059] shrink-0 mt-0.5" /> Apartment or single-family options</li>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#C5A059] shrink-0 mt-0.5" /> Temporary housing resources</li>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#C5A059] shrink-0 mt-0.5" /> Affordable housing option referrals</li>
                    <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#C5A059] shrink-0 mt-0.5" /> Connections to veteran-serving organizations</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-[#E5E3DB] bg-white p-5 text-xs text-[#64748B] leading-relaxed">
                  <strong className="text-[#0B1B3D]">Important:</strong> We do not
                  guarantee housing placement. Our goal is to help create
                  connections between veterans, organizations, and available
                  housing resources. Housing availability, approval, lease terms,
                  deposits, and rental requirements are determined by individual
                  property owners and housing providers.
                </div>
              </div>
              <div className="lg:col-span-7">
                <VeteranForm />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="provider" className="mt-0 focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/10 p-6">
                  <h3 className="font-display text-2xl text-[#0B1B3D] mb-3">
                    Benefits of becoming a Housing Partner
                  </h3>
                  <ul className="space-y-2 text-sm text-[#0F172A]">
                    {PARTNER_BENEFITS.map((b, i) => (
                      <li key={i} className="flex gap-2">
                        <CheckCircle2 className="h-4 w-4 text-[#2C5545] shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-[#E5E3DB] bg-white p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#2C5545] font-semibold mb-3">
                    Who we're seeking
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {[
                      "Private landlords",
                      "Real estate investors",
                      "Apartment communities",
                      "Property management companies",
                      "Developers",
                      "Housing organizations",
                    ].map((x) => (
                      <span key={x} className="rounded-full bg-[#F3F2EE] text-[#0B1B3D] px-3 py-1">{x}</span>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-[#E5E3DB] bg-[#0B1B3D] text-white p-5 text-xs leading-relaxed flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-[#C5A059] shrink-0 mt-0.5" />
                  <span>
                    Serving housing partners nationwide. Whether you have one door
                    or a portfolio, we want to hear from you.
                  </span>
                </div>
              </div>
              <div className="lg:col-span-7">
                <ProviderForm />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">{label}</Label>
      <div className="mt-1.5 [&_input]:h-11">{children}</div>
    </div>
  );
}
